import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Alert,
} from '@mui/material';
import { DataTable, PageHeader, FilterTabs, StatusChip, Column } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import { FxTransaction, FxVendor, FxCategory } from '@/types/api/finance';

type Props = {
  transactions: FxTransaction[];
  vendors: FxVendor[];
  categories: FxCategory[];
  isLoading: boolean;
  counts: { all: number; pending: number; reviewed: number; approved: number };
  activeTab: string;
  onTabChange: (tab: string) => void;
  onApprove: (id: string) => Promise<void>;
  onMarkReviewed: (id: string) => Promise<void>;
  onBulkApprove: (ids: string[]) => Promise<void>;
  onApproveHighConfidence: () => Promise<number>;
  onUpdateTransaction: (id: string, data: Record<string, unknown>) => Promise<void>;
  onExport: (startDate: string, endDate: string) => Promise<void>;
};

const formatCurrency = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const confidenceStatusMap = {
  high: { label: 'High', color: '#10B981' },
  medium: { label: 'Medium', color: '#F59E0B' },
  low: { label: 'Low', color: '#EF4444' },
  manual: { label: 'Manual', color: '#71717A' },
};

const reviewStatusMap = {
  pending: { label: 'Pending', color: '#F59E0B' },
  reviewed: { label: 'Reviewed', color: '#054AD9' },
  approved: { label: 'Approved', color: '#10B981' },
};

function getConfidenceKey(confidence: number | null): string {
  if (confidence == null) return 'manual';
  if (confidence >= 0.9) return 'high';
  if (confidence >= 0.7) return 'medium';
  return 'low';
}

const FinanceXReviewQueueComponent = function ({
  transactions,
  vendors,
  categories,
  isLoading,
  counts,
  activeTab,
  onTabChange,
  onApprove,
  onMarkReviewed,
  onBulkApprove,
  onApproveHighConfidence,
  onUpdateTransaction,
  onExport,
}: Props): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [prevTab, setPrevTab] = useState(activeTab);
  if (prevTab !== activeTab) {
    setPrevTab(activeTab);
    if (selectedIds.size > 0) setSelectedIds(new Set());
  }
  const [editingTx, setEditingTx] = useState<FxTransaction | null>(null);
  const [editVendorId, setEditVendorId] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [exportEnd, setExportEnd] = useState(() => new Date().toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);

  const openEdit = (tx: FxTransaction): void => {
    setEditingTx(tx);
    setEditVendorId(tx.vendorId ?? '');
    setEditCategoryId(tx.categoryId ?? '');
    setEditNotes(tx.notes ?? '');
  };

  const handleSaveAndReview = async (): Promise<void> => {
    if (!editingTx) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await onUpdateTransaction(editingTx.id, {
        vendorId: editVendorId || null,
        categoryId: editCategoryId || null,
        notes: editNotes || null,
      });
      setEditingTx(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save transaction.';
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndApprove = async (): Promise<void> => {
    if (!editingTx) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await onUpdateTransaction(editingTx.id, {
        vendorId: editVendorId || null,
        categoryId: editCategoryId || null,
        notes: editNotes || null,
      });
      await onApprove(editingTx.id);
      setEditingTx(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save and approve transaction.';
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkApproveSelected = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    setIsBulkApproving(true);
    try {
      await onBulkApprove(ids);
      setSelectedIds(new Set());
      setBulkResult(`${ids.length} transaction${ids.length !== 1 ? 's' : ''} approved.`);
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleApproveHighConfidence = async (): Promise<void> => {
    setIsBulkApproving(true);
    try {
      const count = await onApproveHighConfidence();
      setBulkResult(`${count} high-confidence transaction${count !== 1 ? 's' : ''} approved.`);
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    try {
      await onExport(exportStart, exportEnd);
      setShowExport(false);
    } finally {
      setIsExporting(false);
    }
  };

  const tabs = [
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'reviewed', label: 'Reviewed', count: counts.reviewed },
    { key: 'approved', label: 'Approved', count: counts.approved },
    { key: 'all', label: 'All', count: counts.all },
  ];

  const columns: Column<FxTransaction>[] = [
    {
      id: 'date',
      label: 'Date',
      width: '95px',
      render: (tx) => <span style={{ color: '#71717A', fontSize: 13 }}>{tx.date}</span>,
    },
    {
      id: 'description',
      label: 'Description',
      render: (tx) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {tx.description}
        </span>
      ),
    },
    {
      id: 'vendor',
      label: 'Vendor',
      render: (tx) => (
        <span style={{ fontSize: 13, color: tx.vendorId ? '#09090B' : '#EF4444' }}>
          {tx.vendor?.displayName || tx.vendor?.name || 'Uncategorized'}
        </span>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (tx) =>
        tx.category ? (
          <Chip
            label={tx.category.name}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: `${tx.category.color ?? '#6B7280'}20`,
              color: tx.category.color ?? '#6B7280',
            }}
          />
        ) : (
          <span style={{ color: '#EF4444', fontSize: 12 }}>No category</span>
        ),
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right',
      render: (tx) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            color: tx.type === 'income' ? '#10B981' : '#09090B',
          }}
        >
          {tx.type === 'income' ? '+' : '-'}
          {formatCurrency(tx.amount, tx.currency)}
        </span>
      ),
    },
    {
      id: 'confidence',
      label: 'Confidence',
      align: 'center',
      width: '100px',
      render: (tx) => (
        <StatusChip
          status={getConfidenceKey(tx.aiConfidence)}
          statusMap={confidenceStatusMap}
        />
      ),
    },
    {
      id: 'reviewStatus',
      label: 'Status',
      align: 'center',
      width: '100px',
      render: (tx) => (
        <StatusChip status={tx.reviewStatus} statusMap={reviewStatusMap} />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '100px',
      render: (tx) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
          {tx.reviewStatus !== 'approved' && (
            <>
              <Tooltip title="Mark Reviewed">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkReviewed(tx.id);
                  }}
                  sx={{ color: '#054AD9' }}
                >
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove(tx.id);
                  }}
                  sx={{ color: '#10B981' }}
                >
                  <CheckIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Review Queue"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TrioButton
              variantStyle="outline"
              variantColor="gray"
              size="small"
              onClick={() => setShowExport(true)}
            >
              <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Export for CPA
            </TrioButton>
            <TrioButton
              variantColor="blue"
              size="small"
              onClick={handleApproveHighConfidence}
              disabled={isBulkApproving}
            >
              {isBulkApproving ? (
                <CircularProgress size={16} sx={{ mr: 0.5 }} />
              ) : (
                <DoneAllIcon sx={{ fontSize: 16, mr: 0.5 }} />
              )}
              Approve High Confidence
            </TrioButton>
          </Box>
        }
      />

      {bulkResult && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: '8px' }}
          onClose={() => setBulkResult(null)}
        >
          {bulkResult}
        </Alert>
      )}

      <FilterTabs tabs={tabs} activeKey={activeTab} onChange={onTabChange} />

      <DataTable<FxTransaction>
        columns={columns}
        data={transactions}
        rowKey={(tx) => tx.id}
        isLoading={isLoading}
        searchPlaceholder="Search by description or vendor..."
        searchFields={(tx) =>
          `${tx.description} ${tx.vendor?.name ?? ''} ${tx.vendor?.displayName ?? ''}`
        }
        emptyMessage="No transactions to review."
        onRowClick={openEdit}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectionActions={
          <Button
            size="small"
            variant="outlined"
            startIcon={
              isBulkApproving ? <CircularProgress size={14} /> : <DoneAllIcon />
            }
            disabled={isBulkApproving}
            onClick={handleBulkApproveSelected}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              color: '#10B981',
              borderColor: '#10B981',
            }}
          >
            Approve ({selectedIds.size})
          </Button>
        }
      />

      {/* Edit Transaction Dialog */}
      <Dialog
        open={!!editingTx}
        onClose={() => setEditingTx(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Review Transaction</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: '8px' }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {editingTx && (
            <>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  backgroundColor: '#F4F4F5',
                  fontSize: 13,
                }}
              >
                <Box sx={{ fontWeight: 500, mb: 0.5 }}>
                  {editingTx.description}
                </Box>
                <Box sx={{ color: '#71717A' }}>
                  {editingTx.date} &bull;{' '}
                  {editingTx.type === 'income' ? '+' : '-'}
                  {formatCurrency(editingTx.amount)} &bull; Source:{' '}
                  {editingTx.source}
                  {editingTx.aiConfidence != null &&
                    ` \u2022 AI confidence: ${Math.round(editingTx.aiConfidence * 100)}%`}
                </Box>
              </Box>
              <TextField
                label="Vendor"
                select
                fullWidth
                size="small"
                value={editVendorId}
                onChange={(e) => setEditVendorId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.displayName || v.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Category"
                select
                fullWidth
                size="small"
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} ({cat.type})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Notes"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setEditingTx(null)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <TrioButton
            variantStyle="outline"
            variantColor="blue"
            size="small"
            onClick={handleSaveAndReview}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={16} /> : 'Save & Mark Reviewed'}
          </TrioButton>
          <TrioButton
            variantColor="blue"
            size="small"
            onClick={handleSaveAndApprove}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={16} /> : 'Save & Approve'}
          </TrioButton>
        </DialogActions>
      </Dialog>

      {/* Export for CPA Dialog */}
      <Dialog
        open={showExport}
        onClose={() => setShowExport(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Export for CPA</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: '8px' }}>
            Only <strong>approved</strong> transactions will be exported. Review
            and approve all transactions before exporting.
          </Alert>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={exportStart}
            onChange={(e) => setExportStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={exportEnd}
            onChange={(e) => setExportEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowExport(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <TrioButton
            variantColor="blue"
            size="small"
            onClick={handleExport}
            disabled={isExporting || !exportStart || !exportEnd}
          >
            {isExporting ? (
              <CircularProgress size={16} />
            ) : (
              <>
                <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Download CSV
              </>
            )}
          </TrioButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinanceXReviewQueueComponent;
